import {
  Component, Input, Output, EventEmitter, ViewChild,
  OnChanges, SimpleChanges, forwardRef, AfterViewInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DxDataGridComponent, DxDropDownBoxComponent } from 'devextreme-angular';

/** Colonna della griglia interna. */
export interface GridSelectColumn {
  dataField: string;
  caption?: string;
  dataType?: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'object';
  width?: number | string;
  /** Icona prima del valore, sulle righe in cui il campo `field` è valorizzato. */
  icon?: { src: string; field: string };
}

/**
 * Select singolo con dropdown contenente una dx-data-grid filtrabile per colonna.
 * I dati arrivano dal parent via [dataSource].
 * Espone un valore scalare sia via [(value)] sia come ControlValueAccessor.
 * Internamente box e griglia lavorano ad array di righe intere.
 */
@Component({
  selector: 'app-grid-select-box',
  templateUrl: './grid-select-box.component.html',
  styleUrls: ['./grid-select-box.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GridSelectBoxComponent),
      multi: true,
    },
  ],
})
export class GridSelectBoxComponent implements OnChanges, AfterViewInit, ControlValueAccessor {

  @ViewChild('ddBox', { static: false }) ddBox: DxDropDownBoxComponent;
  @ViewChild('ddGrid', { static: false }) ddGrid: DxDataGridComponent;

  /** Righe da mostrare. */
  @Input() dataSource: any[] = [];
  /** Campo chiave delle righe, esposto all'esterno come valore. */
  @Input() keyExpr = 'id';
  /** Campo o funzione mostrati nel box quando c'è una selezione. */
  @Input() displayExpr: string | ((row: any) => string) = 'name';
  /** Colonne della griglia; il parent deve passarne una reference stabile. */
  @Input() columns: GridSelectColumn[] = [{ dataField: 'name' }];
  @Input() placeholder = 'Seleziona…';
  @Input() disabled = false;
  @Input() gridHeight = 250;
  @Input() showClearButton = true;
  @Input() noDataText = 'Nessun dato disponibile';
  /** Messaggio della regola obbligatoria (gruppo di validazione di default); senza, nessun validatore. */
  @Input() requiredMessage: string = null;
  /** Larghezza della tendina, letta all'avvio; senza, quella del box. Resta sempre dentro lo schermo. */
  @Input() dropDownWidth: number | string = null;

  /** Valore scalare selezionato. */
  @Input() value: any = null;
  @Output() valueChange = new EventEmitter<any>();
  /** Emesso solo su selezione o clear dell'utente. */
  @Output() selectionChanged = new EventEmitter<{ value: any; row: any }>();

  /** Reference stabili: evitano di ricostruire la griglia a ogni change detection. */
  public readonly selection = { mode: 'single' };
  public readonly filterRow = { visible: true };
  public readonly scrolling = { mode: 'virtual' };

  /** Colonne passate alla griglia: quelle con l'icona usano il template `iconCellTemplate`. */
  public gridColumns: any[] = this.columns;

  /** Stato interno: array con la riga selezionata. */
  public gridBoxValue: any[] = [];

  private onChange: (v: any) => void = () => { };
  private onTouched: () => void = () => { };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.gridColumns = this.columns.map(c => c.icon ? { ...c, cellTemplate: 'iconCellTemplate' } : c);
    }
    if (changes['value'] || changes['dataSource']) {
      this.syncSelectionFromValue();
    }
  }

  /** Con `dropDownWidth`: tendina larga così, al massimo quanto lo schermo, spostata per restarci dentro. */
  ngAfterViewInit(): void {
    if (this.dropDownWidth == null || !this.ddBox?.instance) {
      return;
    }

    this.ddBox.instance.option('dropDownOptions', {
      showTitle: false,
      width: this.dropDownWidth,
      maxWidth: 'calc(100vw - 32px)',
      position: {
        my: 'left top',
        at: 'left bottom',
        offset: { x: 0, y: -1 },
        collision: { x: 'flipfit', y: 'flip' },
        of: this.ddBox.instance.element(),
      },
    });
  }

  /** Risolve il valore scalare nella riga corrispondente. */
  private syncSelectionFromValue(): void {
    if (this.value === null || this.value === undefined || !this.dataSource?.length) {
      this.gridBoxValue = [];
      return;
    }
    const row = this.dataSource.find(
      r => String(r?.[this.keyExpr]) === String(this.value)
    );
    this.gridBoxValue = row ? [row] : [];
  }

  /** displayExpr del box: dalla riga selezionata all'etichetta. */
  public boxDisplayExpr = (item: any): string => {
    const row = Array.isArray(item) ? item[0] : item;
    if (!row) return '';
    return typeof this.displayExpr === 'function'
      ? this.displayExpr(row)
      : (row?.[this.displayExpr] ?? '');
  };

  /** Selezione o clear nella griglia: aggiorna il valore, emette e chiude il box. */
  public onGridSelectionChanged(e: any): void {
    const row = e?.selectedRowsData?.length ? e.selectedRowsData[0] : null;
    const newValue = row ? row[this.keyExpr] : null;

    // Griglia svuotata prima che arrivi il dataSource: è una sincronizzazione programmatica,
    // non un clear dell'utente, e non deve azzerare il valore del parent.
    if (!row && !this.dataSource?.length) {
      return;
    }

    // `null` e `undefined` sono lo stesso "nessun valore": confrontarli come stringhe li rende
    // diversi, si emette un valueChange fasullo e il model del parent cambia dopo il check
    // di Angular, causando NG0100.
    const current = this.value ?? null;
    const next = newValue ?? null;
    const unchanged = next === current || (next !== null && current !== null && String(next) === String(current));
    if (unchanged) {
      // nessun cambiamento effettivo: chiude solo se c'è una riga
      if (row && this.ddBox?.instance) this.ddBox.instance.close();
      return;
    }

    if (!row) this.gridBoxValue = []; // normalizza dopo un clear
    this.value = newValue;
    this.onChange(newValue);
    this.onTouched();
    this.valueChange.emit(newValue);
    this.selectionChanged.emit({ value: newValue, row });

    if (row && this.ddBox?.instance) this.ddBox.instance.close();
  }

  /** Clear del box fatto dall'utente: azzera il valore senza passare dalla griglia. */
  public onBoxValueChanged(e: any): void {
    const isEmpty = e?.value == null || (Array.isArray(e.value) && e.value.length === 0);
    if (!isEmpty || !e?.event || (this.value ?? null) === null) {
      return;
    }

    this.gridBoxValue = [];
    this.ddGrid?.instance.clearSelection();
    this.value = null;
    this.onChange(null);
    this.onTouched();
    this.valueChange.emit(null);
    this.selectionChanged.emit({ value: null, row: null });
  }

  // --- ControlValueAccessor ---
  writeValue(v: any): void {
    this.value = v;
    this.syncSelectionFromValue();
  }
  registerOnChange(fn: (v: any) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
